import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  getLibraryUsers,
  getCurrentUser,
  getProfile,
  upsertLibraryEntry,
  updateLibraryEntry,
  getLibraryEntry,
} from '../utils/supabase';

const Library = () => {
  const [users, setUsers] = useState([]);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inLibrary, setInLibrary] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [timeSpent, setTimeSpent] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    loadUserAndLibrary();
    const interval = setInterval(() => {
      loadLibraryUsers();
      updateTimeSpent();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadUserAndLibrary = async () => {
    const { user } = await getCurrentUser();
    setUser(user);
    if (user) {
      const { data } = await getProfile(user.id);
      setProfile(data);
      await checkLibraryStatus(user.id);
    }
    await loadLibraryUsers();
    setLoading(false);
  };

  const checkLibraryStatus = async (userId) => {
    const { data } = await getLibraryEntry(userId);
    if (data) {
      setInLibrary(data.is_active);
      setStartTime(data.current_start_time);
      // Parse time_spent
      if (data.time_spent) {
        const parsed = typeof data.time_spent === 'string' 
          ? JSON.parse(data.time_spent) 
          : data.time_spent;
        setTimeSpent(parsed || {});
      }
    }
  };

  const loadLibraryUsers = async () => {
    const { data, error } = await getLibraryUsers();
    if (error) {
      console.error('Error loading library users:', error);
      return;
    }
    setUsers(data || []);
  };

  const updateTimeSpent = () => {
    // Update time spent display for each user
    const updated = {};
    users.forEach((u) => {
      if (u.current_start_time && u.is_active) {
        const start = new Date(u.current_start_time);
        const now = new Date();
        const diff = Math.floor((now - start) / 1000);
        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        const seconds = diff % 60;
        updated[u.user_id] = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      }
    });
    setTimeSpent(updated);
  };

  const handleJoinLibrary = async () => {
    if (!user) {
      setError('Please sign in to join the library');
      return;
    }

    const now = new Date().toISOString();
    const today = new Date().toISOString().split('T')[0];

    const entry = {
      user_id: user.id,
      date: today,
      is_active: true,
      current_start_time: now,
      time_spent: {},
    };

    const { data, error } = await upsertLibraryEntry(entry);
    if (error) {
      setError(error.message);
      return;
    }

    setInLibrary(true);
    setStartTime(now);
    setError('');
    await loadLibraryUsers();
  };

  const handleLeaveLibrary = async () => {
    if (!user) return;

    const now = new Date().toISOString();
    const today = new Date().toISOString().split('T')[0];

    // Calculate time spent for this session
    if (startTime) {
      const start = new Date(startTime);
      const end = new Date(now);
      const diffSeconds = Math.floor((end - start) / 1000);
      
      // Update time_spent
      const currentTimeSpent = { ...timeSpent };
      const key = `${startTime}`;
      currentTimeSpent[key] = (currentTimeSpent[key] || 0) + diffSeconds;
      
      const { error } = await updateLibraryEntry(user.id, {
        is_active: false,
        current_start_time: null,
        time_spent: currentTimeSpent,
      });

      if (!error) {
        setInLibrary(false);
        setStartTime(null);
        setTimeSpent(currentTimeSpent);
        await loadLibraryUsers();
      }
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getTotalTimeSpent = (userId) => {
    // For current users, show ongoing time
    if (timeSpent[userId]) {
      return timeSpent[userId];
    }
    // For users in library, calculate from their start time
    const user = users.find(u => u.user_id === userId);
    if (user?.is_active && user?.current_start_time) {
      const start = new Date(user.current_start_time);
      const now = new Date();
      const diff = Math.floor((now - start) / 1000);
      const hours = Math.floor(diff / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return '--:--:--';
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] pb-24">
      <div className="container-custom py-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold">📚 Library</h1>
            <p className="text-gray-500 text-sm">
              {users.filter(u => u.is_active).length} users currently active
            </p>
          </div>
          {user ? (
            <button
              onClick={inLibrary ? handleLeaveLibrary : handleJoinLibrary}
              className={`px-4 py-2 rounded-lg transition ${
                inLibrary
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : 'bg-primary text-white hover:bg-primary/90'
              }`}
            >
              {inLibrary ? 'Leave Library' : 'Join Library'}
            </button>
          ) : (
            <Link
              to="/auth"
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition"
            >
              Sign in to Join
            </Link>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            {error}
            <button onClick={() => setError('')} className="ml-2">✕</button>
          </div>
        )}

        {!user && (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-semibold mb-2">Join the Library</h2>
            <p className="text-gray-500 mb-4">Sign in to see who's in the library and connect with others</p>
            <Link
              to="/auth"
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition"
            >
              Sign In
            </Link>
          </div>
        )}

        {user && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {users.length === 0 ? (
              <div className="col-span-full text-center py-12 text-gray-500">
                <div className="text-4xl mb-4">📚</div>
                <p>No one is in the library yet. Be the first!</p>
              </div>
            ) : (
              users.map((libraryUser) => (
                <div
                  key={libraryUser.user_id}
                  className={`bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition ${
                    libraryUser.user_id === user?.id ? 'ring-2 ring-primary' : ''
                  }`}
                >
                  <Link to={`/profile/${libraryUser.user_id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-semibold text-lg flex-shrink-0">
                        {getInitials(libraryUser.profiles?.full_name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {libraryUser.profiles?.full_name || 'Unknown User'}
                          {libraryUser.user_id === user?.id && (
                            <span className="ml-2 text-xs text-primary">(You)</span>
                          )}
                        </p>
                        <div className="flex items-center gap-2">
                          <span className={`inline-block w-2 h-2 rounded-full ${
                            libraryUser.is_active ? 'bg-green-500 pulse-dot' : 'bg-gray-300'
                          }`}></span>
                          <span className="text-xs text-gray-500">
                            {libraryUser.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Time spent</span>
                        <span className="font-mono font-medium">
                          {getTotalTimeSpent(libraryUser.user_id)}
                        </span>
                      </div>
                    </div>
                  </Link>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Library;
